import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  ClockIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

// Comprehensive global timezones list organized by region
const TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', group: 'UTC' },
  
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York', group: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago', group: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver', group: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles', group: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time - Anchorage', group: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time - Honolulu', group: 'Americas' },
  { value: 'America/Phoenix', label: 'Arizona Time - Phoenix', group: 'Americas' },
  { value: 'America/Toronto', label: 'Eastern Time - Toronto', group: 'Americas' },
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver', group: 'Americas' },
  { value: 'America/Mexico_City', label: 'Central Time - Mexico City', group: 'Americas' },
  { value: 'America/Bogota', label: 'Colombia Time - Bogota', group: 'Americas' },
  { value: 'America/Lima', label: 'Peru Time - Lima', group: 'Americas' },
  { value: 'America/Santiago', label: 'Chile Time - Santiago', group: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'Argentina Time - Buenos Aires', group: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time - Sao Paulo', group: 'Americas' },
  { value: 'America/Caracas', label: 'Venezuela Time - Caracas', group: 'Americas' },
  { value: 'America/Halifax', label: 'Atlantic Time - Halifax', group: 'Americas' },
  { value: 'America/St_Johns', label: 'Newfoundland Time - St. Johns', group: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'GMT/BST - London', group: 'Europe' },
  { value: 'Europe/Dublin', label: 'Ireland Time - Dublin', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time - Paris', group: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time - Berlin', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Central European Time - Amsterdam', group: 'Europe' },
  { value: 'Europe/Brussels', label: 'Central European Time - Brussels', group: 'Europe' },
  { value: 'Europe/Zurich', label: 'Central European Time - Zurich', group: 'Europe' },
  { value: 'Europe/Vienna', label: 'Central European Time - Vienna', group: 'Europe' },
  { value: 'Europe/Rome', label: 'Central European Time - Rome', group: 'Europe' },
  { value: 'Europe/Madrid', label: 'Central European Time - Madrid', group: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Western European Time - Lisbon', group: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Central European Time - Stockholm', group: 'Europe' },
  { value: 'Europe/Oslo', label: 'Central European Time - Oslo', group: 'Europe' },
  { value: 'Europe/Copenhagen', label: 'Central European Time - Copenhagen', group: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time - Helsinki', group: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Central European Time - Warsaw', group: 'Europe' },
  { value: 'Europe/Prague', label: 'Central European Time - Prague', group: 'Europe' },
  { value: 'Europe/Budapest', label: 'Central European Time - Budapest', group: 'Europe' },
  { value: 'Europe/Athens', label: 'Eastern European Time - Athens', group: 'Europe' },
  { value: 'Europe/Bucharest', label: 'Eastern European Time - Bucharest', group: 'Europe' },
  { value: 'Europe/Kiev', label: 'Eastern European Time - Kyiv', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow Time - Moscow', group: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Turkey Time - Istanbul', group: 'Europe' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Gulf Standard Time - Dubai', group: 'Asia' },
  { value: 'Asia/Riyadh', label: 'Arabian Time - Riyadh', group: 'Asia' },
  { value: 'Asia/Tehran', label: 'Iran Time - Tehran', group: 'Asia' },
  { value: 'Asia/Karachi', label: 'Pakistan Time - Karachi', group: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India Standard Time - Mumbai/Delhi', group: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Time - Dhaka', group: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Indochina Time - Bangkok', group: 'Asia' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Indochina Time - Ho Chi Minh', group: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time - Jakarta', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore Time - Singapore', group: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time - Kuala Lumpur', group: 'Asia' },
  { value: 'Asia/Manila', label: 'Philippine Time - Manila', group: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time - Hong Kong', group: 'Asia' },
  { value: 'Asia/Shanghai', label: 'China Standard Time - Shanghai', group: 'Asia' },
  { value: 'Asia/Taipei', label: 'Taiwan Time - Taipei', group: 'Asia' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time - Seoul', group: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time - Tokyo', group: 'Asia' },
  
  // Australia & Pacific
  { value: 'Australia/Perth', label: 'Australian Western Time - Perth', group: 'Australia & Pacific' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time - Adelaide', group: 'Australia & Pacific' },
  { value: 'Australia/Darwin', label: 'Australian Central Time - Darwin', group: 'Australia & Pacific' },
  { value: 'Australia/Brisbane', label: 'Australian Eastern Time - Brisbane', group: 'Australia & Pacific' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time - Sydney', group: 'Australia & Pacific' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time - Melbourne', group: 'Australia & Pacific' },
  { value: 'Australia/Hobart', label: 'Australian Eastern Time - Hobart', group: 'Australia & Pacific' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time - Auckland', group: 'Australia & Pacific' },
  { value: 'Pacific/Fiji', label: 'Fiji Time - Suva', group: 'Australia & Pacific' },
  { value: 'Pacific/Guam', label: 'Chamorro Time - Guam', group: 'Australia & Pacific' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Egypt Time - Cairo', group: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'South Africa Time - Johannesburg', group: 'Africa' },
  { value: 'Africa/Lagos', label: 'West Africa Time - Lagos', group: 'Africa' },
  { value: 'Africa/Nairobi', label: 'East Africa Time - Nairobi', group: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Morocco Time - Casablanca', group: 'Africa' },
  { value: 'Africa/Algiers', label: 'Central European Time - Algiers', group: 'Africa' },
  { value: 'Africa/Accra', label: 'Ghana Mean Time - Accra', group: 'Africa' },
  
  // Middle East
  { value: 'Asia/Jerusalem', label: 'Israel Time - Jerusalem', group: 'Middle East' },
  { value: 'Asia/Beirut', label: 'Lebanon Time - Beirut', group: 'Middle East' },
  { value: 'Asia/Baghdad', label: 'Arabian Time - Baghdad', group: 'Middle East' },
  { value: 'Asia/Kuwait', label: 'Arabian Time - Kuwait', group: 'Middle East' },
  { value: 'Asia/Qatar', label: 'Arabian Time - Doha', group: 'Middle East' },
  
  // Atlantic
  { value: 'Atlantic/Azores', label: 'Azores Time - Azores', group: 'Atlantic' },
  { value: 'Atlantic/Reykjavik', label: 'Iceland Time - Reykjavik', group: 'Atlantic' },
  { value: 'Atlantic/Bermuda', label: 'Atlantic Time - Bermuda', group: 'Atlantic' }
];

// Group timezones for optgroup display
const TIMEZONE_GROUPS = ['UTC', 'Americas', 'Europe', 'Asia', 'Australia & Pacific', 'Africa', 'Middle East', 'Atlantic'];

export default function CompanySettings() {
  const { getCurrentCompany } = useAuthStore();
  const currentCompany = getCurrentCompany();
  const companyId = currentCompany?._id || currentCompany;
  const queryClient = useQueryClient();
  
  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => apiHelpers.getCompany(companyId),
    enabled: !!companyId,
    retry: 1
  });
  
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    settings: {
      defaultTimezone: 'UTC',
      dateFormat: 'YYYY-MM-DD',
      allowExternalSharing: false
    }
  });
  
  useEffect(() => {
    if (data?.data?.company) {
      const c = data.data.company;
      setFormData({
        name: c.name || '',
        industry: c.industry || '',
        website: c.website || '',
        settings: c.settings || {
          defaultTimezone: 'UTC',
          dateFormat: 'YYYY-MM-DD',
          allowExternalSharing: false
        }
      });
    }
  }, [data]);
  
  const updateMutation = useMutation({
    mutationFn: (updates) => apiHelpers.updateCompany(companyId, updates),
    onSuccess: () => {
      toast.success('Settings saved');
      queryClient.invalidateQueries(['company', companyId]);
    },
    onError: (error) => {
      console.error('Update company error:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    }
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };
  
  // Show message if no company selected (for Super Admins and Managing Admins)
  if (!companyId) {
    return (
      <div className="max-w-3xl animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Company Settings
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Manage your company profile and preferences
          </p>
        </div>
        <div className="card p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <BuildingOfficeIcon className="w-12 h-12 text-surface-400 mb-4" />
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              No Company Selected
            </h3>
            <p className="text-surface-500 dark:text-surface-400 max-w-md">
              Please select a company from the sidebar to manage its settings.
            </p>
            <a 
              href="/admin/companies" 
              className="btn-primary mt-4"
            >
              Select a Company
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-8" />
        <div className="card p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton h-12" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="max-w-3xl animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Company Settings
          </h1>
        </div>
        <div className="card p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <BuildingOfficeIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              Failed to Load Company
            </h3>
            <p className="text-surface-500 dark:text-surface-400 max-w-md">
              {error?.response?.data?.error || error?.message || 'Unable to load company settings. Please try again.'}
            </p>
            <button 
              onClick={() => queryClient.invalidateQueries(['company', companyId])}
              className="btn-primary mt-4"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const companyData = data?.data?.company;
  
  return (
    <div className="max-w-3xl animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
          Company Settings
        </h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Manage your company profile and preferences
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Profile */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-3">
            <BuildingOfficeIcon className="w-5 h-5 text-surface-500" />
            <h2 className="font-medium text-surface-900 dark:text-white">Company Profile</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="label">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
              />
            </div>
            
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="e.g., Technology, Healthcare, Finance"
                className="input"
              />
            </div>
            
            <div>
              <label className="label">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://example.com"
                className="input"
              />
            </div>
          </div>
        </div>
        
        {/* Preferences */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-3">
            <Cog6ToothIcon className="w-5 h-5 text-surface-500" />
            <h2 className="font-medium text-surface-900 dark:text-white">Preferences</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="label">Default Timezone</label>
              <select
                value={formData.settings.defaultTimezone}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, defaultTimezone: e.target.value }
                })}
                className="input"
              >
                {TIMEZONE_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-surface-500 mt-1">
                This timezone will be used for AI responses and date/time formatting
              </p>
            </div>
            
            <div>
              <label className="label">Date Format</label>
              <select
                value={formData.settings.dateFormat}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, dateFormat: e.target.value }
                })}
                className="input"
              >
                <option value="YYYY-MM-DD">2024-01-15</option>
                <option value="MM/DD/YYYY">01/15/2024</option>
                <option value="DD/MM/YYYY">15/01/2024</option>
                <option value="MMM DD, YYYY">Jan 15, 2024</option>
              </select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-surface-900 dark:text-white">
                  Allow External Sharing
                </label>
                <p className="text-sm text-surface-500">
                  Enable public dashboard links
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  settings: { ...formData.settings, allowExternalSharing: !formData.settings.allowExternalSharing }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.settings.allowExternalSharing ? 'bg-primary-600' : 'bg-surface-200 dark:bg-surface-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.settings.allowExternalSharing ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        
        {/* Plan Info */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">Current Plan</h3>
              <p className="text-sm text-surface-500">{companyData?.billingPlan?.toUpperCase() || 'FREE'}</p>
            </div>
            <span className="badge-primary capitalize">
              {companyData?.billingPlan || 'free'}
            </span>
          </div>
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending && (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}

